import { InfoCircle } from '@carespace-icons/general/infoCircle';
import { evalScore } from '@pages/AiAssistantCustomRomScanResult/jointsTemplateData';
import { router } from '@routers/routers';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import { ICustomRomSession, IRomPatientResult } from '@stores/interfaces';
import {
	getProgramByID,
	resetAll,
	selectExercise,
	updateSessionTitle,
} from '@stores/rom/main';
import {
	Button,
	Checkbox,
	Empty,
	Flex,
	Image,
	Input,
	message,
	Modal,
	Popover,
	Spin,
	Table,
	Tag,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface SendResultByEditModalProps {
	modalOpen: boolean;
	setModalOpen: (val: boolean) => void;
	closable: boolean;
	selectedRom: ICustomRomSession;
	completedStatus: boolean;
	fetchSessionData?: (sessionId: string, page: number, completed: boolean) => void;
}

export const SummaryResultEditModal = (props: SendResultByEditModalProps) => {
	const { completedStatus, modalOpen, setModalOpen, closable, selectedRom, fetchSessionData } =
		props;
	const user = useTypedSelector(state => state.user);
	const navigate = useNavigate();
	const { selectedUser } = useTypedSelector(state => state.contacts.main);
	const { t } = useTranslation();
	const [sectionName, setSectionName] = useState<string>();
	const [sessionData, setSessionData] = useState<{
		exercises?: { id: string }[];
		id?: string;
	}>({ exercises: [], id: '' });
	const [resultsList, setResultsList] = useState<IRomPatientResult[]>([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
	const dispatch = useTypedDispatch();
	const [missingExercisesLength, setMissingExercisesLength] = useState(0)
	const [isUpdating, setIsUpdating] = useState<boolean>(false);

	useEffect(() => {
	const fetchData = async (id: string) => {
		const data = await dispatch(getProgramByID(id));
		const programData = (data.payload as { data: { exercises?: { id: string, normal : number, wfl: number }[]; id?: string } }).data;

		setSessionData(programData);

		const initialResults =
			selectedRom?.romPatientResults
				?.flatMap(r =>
					(r?.results ?? []).map(result => ({
						...result,
						screenshot: r.screenshot,
						score: Math.ceil(evalScore(result)),
					})),
				)
				?.sort((a, b) => b.score - a.score) || [];

		const existingExerciseIds = new Set(initialResults.map(r => r.romProgramExerciseId));

		const missingExercises = (programData.exercises ?? [])
			.filter(ex => !existingExerciseIds.has(ex.id))
			.map(ex => ({
				romProgramExerciseId: ex.id,
				normal: ex.normal,
				wfl: ex.wfl,
				value: -1,
				ranking: 0,
				romProgramExercise: ex,
				score: 0,
				screenshot:'/images/empty-image.png'
			}));
		!completedStatus && setSelectedRowKeys(missingExercises.map(item => item.romProgramExerciseId));
		setResultsList([...initialResults, ...missingExercises]);
		setMissingExercisesLength(missingExercises.length)
		setSectionName(selectedRom?.title || '');
	};

	if (selectedRom?.romProgramId) {
		fetchData(selectedRom.romProgramId);
	}
}, [selectedRom]);

	const getColorForScore = (score: number) => {
		if (score >= 90) return '#52c41a';
		if (score >= 75 && score <= 89) return '#73D13D';
		if (score >= 60 && score <= 74) return '#faad14';
		if (score >= 40 && score <= 59) return '#F59712';
		if (score >= 20 && score <= 39) return '#F2615B';
		return '#d9363e';
	};

	const onSelectAllChange = (checked: boolean) => {
		if (checked) {
			const selectableIds = resultsList
				.filter(item =>
					sessionData?.exercises?.some(
						ex => ex.id === item.romProgramExercise.id,
					),
				)
				.map(item => item.romProgramExercise.id);

			setSelectedRowKeys([...new Set(selectableIds)]);
		} else {
			setSelectedRowKeys([]);
		}
	};

	const handleCheckboxChange = (id: string) => {
		setSelectedRowKeys(prev =>
			prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id],
		);
	};

	const calculateRanking = (score: number) => {
		if (score >= 91) return 10;
		if (score >= 81) return 9;
		if (score >= 71) return 8;
		if (score >= 61) return 7;
		if (score >= 51) return 6;
		if (score >= 41) return 5;
		if (score >= 31) return 4;
		if (score >= 21) return 3;
		if (score >= 11) return 2;
		if (score >= 1) return 1;
		return 0;
	};

	const columns = [
		{
			title: '',
			key: 'image',
			align: 'center' as const,
			render: (record: IRomPatientResult) =>
				record?.screenshot ? (
					<Image
						src={"/images/empty-image.png"}
						className="image"
						preview={{
							src: record.screenshot,
							mask: <MdOutlineRemoveRedEye size={18} />,
						}}
						width={35}
						height={30}
					/>
				) : (
					'-'
				),
		},
		{
			title: t('Admin.data.retryScreen.measure'),
			key: 'title',
			render: (record: IRomPatientResult) =>
				record?.romProgramExercise?.strapiOmniRomExercise?.name ||
				record?.romProgramExercise?.exerciseLibrary?.title,
		},

		{
			title: (
				<Popover content={t('Patient.data.omnirom.normalInfo')} title="Normal">
					<Flex align="space-between" gap="small" justify="center">
						<span>{t('Patient.data.omnirom.normal')}</span>
						<span className="cursor-pointer">
							<InfoCircle width={16} height={16} color="stroke-gray-500" />
						</span>
					</Flex>
				</Popover>
			),
			dataIndex: 'normal',
			align: 'center' as const,
		},
		{
			title: (
				<Popover content={t('Patient.data.omnirom.wflInfo')} title="WFL">
					<Flex align="space-between" gap="small" justify="center">
						<span>{t('Patient.data.omnirom.wfl')}</span>
						<span className="cursor-pointer">
							<InfoCircle width={16} height={16} color="stroke-gray-500" />
						</span>
					</Flex>
				</Popover>
			),
			dataIndex: 'wfl',
			align: 'center' as const,
		},
		{
			title: t('Admin.data.retryScreen.value'),
			dataIndex: 'value',
			render: (value: number, record: IRomPatientResult) => (
				<span
					style={{
						color: getColorForScore(evalScore(record) ?? 0),
						fontWeight: 'bold',
					}}>
					{value === -1 ? 0 : value}
				</span>
			),
			align: 'center' as const,
		},
		{
			title: t('Patient.data.omnirom.ranking'),
			dataIndex: 'ranking',
			render: (score: number, record: IRomPatientResult) => (
				<span
					style={{
						color: getColorForScore(evalScore(record) ?? 0),
						fontWeight: 'bold',
					}}>
					{calculateRanking(evalScore(record) ?? 0)}
				</span>
			),
			align: 'center' as const,
		},
		{
			title: t('Admin.data.survey.score'),
			dataIndex: 'score',
			render: (score: number, record: IRomPatientResult) => (
				<Tag
					color={getColorForScore(evalScore(record) ?? 0)}
					style={{
						marginInlineEnd: 0,
						textAlign: 'center',
						width: '35px',
					}}>
					<p>{Math.ceil(evalScore(record)) || '-'}</p>
				</Tag>
			),
			align: 'center' as const,
		},
	...(completedStatus || missingExercisesLength == 0 ? [
		{
			title: (
				<Checkbox
					onChange={e => onSelectAllChange(e.target.checked)}
					className="report-checkbox"
					checked={
						selectedRowKeys.length > 0 &&
						selectedRowKeys.length ===
							resultsList.filter(item =>
								sessionData?.exercises?.some(
									ex => ex.id === item.romProgramExercise.id,
								),
							).length
					}
					indeterminate={
						selectedRowKeys.length > 0 &&
						selectedRowKeys.length <
							resultsList.filter(item =>
								sessionData?.exercises?.some(
									ex => ex.id === item.romProgramExercise.id,
								),
							).length
					}
				/>
			),
			dataIndex: 'select',
			render: (_: string, record: IRomPatientResult) => (
				<Checkbox
					checked={selectedRowKeys.includes(record.romProgramExercise.id)}
					onChange={() => handleCheckboxChange(record.romProgramExercise.id)}
					className="report-checkbox"
					disabled={
						!sessionData?.exercises?.some(
							ex => ex.id === record.romProgramExercise.id,
						)
					}
				/>
			),
			align: 'center' as const,
		}
	] : []),
	];

	const handleUpdateTitle = async () => {
		setIsUpdating(true)
		const payload = {
			id: selectedRom?.id,
			payload: {
				title: sectionName,
				userId: selectedRom?.userId,
				romProgramId: selectedRom?.romProgramId,
				strapiOmniRomProgramId: selectedRom?.strapiOmniRomProgramId,
			},
		};
		await dispatch(updateSessionTitle(payload));
		message.success(t('Admin.data.retryScreen.editedSuccess'));
		fetchSessionData && fetchSessionData(sessionData?.id || '', 1, completedStatus);
		setIsUpdating(false)
	};

	return (
		<Modal
			open={modalOpen}
			centered
			footer={null}
			destroyOnClose
			closable={closable}
			maskClosable={false}
			width={800}
			className="select-none"
			onCancel={() => setModalOpen(false)}
			title={
				<h2 style={{ textAlign: 'center', margin: 0 }}>
					{t('Admin.data.retryScreen.selectTitle')}
				</h2>
			}>
			<p style={{ textAlign: 'center' }}>
				{t('Admin.data.retryScreen.descr1')} <br />{' '}
				{t('Admin.data.retryScreen.descr2')}
			</p>
			<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
				<Input
					placeholder={t('Admin.data.retryScreen.addName')}
					value={sectionName}
					onChange={e => setSectionName(e.target.value)}
				/>
				<Button
					type="primary"
					loading={isUpdating}
					onClick={() => {
						handleUpdateTitle();
					}}
					style={{ boxShadow: 'none', borderRadius: '7px' }}
					disabled={!sectionName?.trim()}>
					{t('Admin.data.survey.save')}
				</Button>
			</div>
			{resultsList?.length >= 1 ? (
				<div style={{ marginTop: '10px' }}>
					<Table
						bordered
						size="small"
						dataSource={resultsList}
						columns={columns}
						pagination={false}
						rowClassName={(record: IRomPatientResult) =>
							record.value === -1 ? 'bg-gray-300' : ''
						}
					/>
					<Flex
						gap={10}
						justify="center"
						align="center"
						className="null-results"
						style={{ marginTop: '10px' }}>
						<Button
							className={'btn-outline !h-auto  !rounded-full'}
							type="primary"
							onClick={() => {
								dispatch(resetAll());
								const selectedExercises = sessionData?.exercises?.filter(
									(item: { id: string }) => selectedRowKeys.includes(item.id),
								);
								dispatch(selectExercise(selectedExercises));
								navigate(
									`/${user?.isPhysioterapist ? selectedUser?.id : user?.id}${router.AIASSISTANT_START_ROM_SCAN}`,
									{
										state: {
											isEdit: true,
											sessionId: selectedRom?.id,
										},
									},
								);
							}}
							style={{ boxShadow: 'none' }}
							disabled={selectedRowKeys.length === 0}>
							{completedStatus || missingExercisesLength == 0 ? (
								<>{t('Admin.data.retryScreen.scanSelected')}</>
							) : (
								<>{t('Admin.data.retryScreen.pendingScans')}</>
							)}
							{selectedRowKeys.length > 0 && (
								<Tag
									color="gold"
									style={{
										marginLeft: 8,
										borderRadius: '999px',
										padding: '0 8px',
										lineHeight: '20px',
										height: '20px',
										background: 'transparent',
										color: '#FFF',
										borderColor: '#fff',
									}}>
									{selectedRowKeys.length}
								</Tag>
							)}
						</Button>
						<Button
							className={'cancel-btn-outline !h-auto  !rounded-full'}
							type="default"
							onClick={() => setModalOpen(false)}
							style={{ boxShadow: 'none' }}>
							{t('Admin.data.retryScreen.cancel')}
						</Button>
					</Flex>
				</div>
			) : (
				<Spin className="spin-css" tip="Loading" size="large" />
			)}
		</Modal>
	);
};
